import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Sistema de cache para reduzir chamadas à API
const cache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutos

const getCacheKey = (type, params) => `${type}:${JSON.stringify(params)}`;

const getFromCache = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) return cached.data;
  if (cached) cache.delete(key);
  return null;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Chama o actor Apify via backend (token nunca exposto no frontend)
const runApifyActor = async (actorId, input) => {
  const response = await axios.post(`${API_URL}/apify/run`, { actorId, input }, {
    timeout: 60000,
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

// Decodifica a URL da Apify para obter a URL original do Instagram
const decodeApifyUrl = (apifyUrl) => {
  try {
    if (!apifyUrl || !apifyUrl.includes('apifyusercontent.com')) return apifyUrl;

    const match = apifyUrl.match(/\/cb:1\/([^.]+)/);
    if (match && match[1]) {
      try {
        const decoded = atob(match[1]);
        if (decoded.startsWith('https://scontent') || decoded.startsWith('http')) return decoded;
      } catch (_) {}
    }
  } catch (_) {}
  return apifyUrl;
};

export const getPrices = async (platform = 'instagram') => {
  try {
    const response = await axios.get(`${API_URL}/prices?platform=${platform}`);
    return response.data;
  } catch (error) {
    console.error('Erro ao buscar preços:', error.message);
    throw error;
  }
};

export const getInstagramPosts = async (username, limit = 9, onlyReels = false) => {
  try {
    if (!username) throw new Error('Username é obrigatório');

    const cacheKey = getCacheKey('instagram-posts', { username, limit, onlyReels });
    const cachedData = getFromCache(cacheKey);
    if (cachedData) return cachedData;

    let usernameClean = username.replace('@', '');
    if (usernameClean.startsWith('http')) {
      const match = usernameClean.match(/instagram\.com\/([^/?]+)/);
      if (match?.[1]) usernameClean = match[1];
    }

    const actorId = onlyReels
      ? 'instagram-scraper~instagram-profile-reels-scraper'
      : 'instagram-scraper~fast-instagram-post-scraper';

    const input = onlyReels
      ? { instagramUsernames: [usernameClean], resultsLimit: limit, memory: 4096 }
      : { instagramUsernames: [usernameClean], postsPerProfile: limit, memory: 4096 };

    let data = await runApifyActor(actorId, input);

    // Fallback: if fast-post-scraper returns empty, try profile scraper's latestPosts
    if (!onlyReels && (!Array.isArray(data) || data.length === 0)) {
      const profileData = await runApifyActor('apify~instagram-profile-scraper', {
        usernames: [usernameClean], maxRequestRetries: 2, resultsLimit: 1, memory: 4096,
      });
      if (Array.isArray(profileData) && profileData.length > 0 && Array.isArray(profileData[0].latestPosts)) {
        data = profileData[0].latestPosts.slice(0, limit);
      }
    }

    if (!Array.isArray(data) || data.length === 0) throw new Error('Nenhum post encontrado');

    const mappedPosts = data.map(post => {
      const imageUrl = post.thumbnail || post.video_thumbnail || post.image || post.display_url || post.displayUrl || post.thumbnailUrl;
      const proxiedImageUrl = imageUrl ? `${API_URL}/proxy-image?url=${encodeURIComponent(imageUrl)}` : null;
      const postUrl = post.reel_url || post.url || (post.shortcode ? `https://www.instagram.com/p/${post.shortcode}/` : '');

      let shortCode = post.shortcode || post.shortCode;
      if (!shortCode && postUrl) {
        const match = postUrl.match(/\/(?:p|reel)\/([^/?]+)/);
        if (match) shortCode = match[1];
      }

      return {
        id: post.id || post.pk,
        shortCode,
        url: postUrl,
        displayUrl: proxiedImageUrl,
        thumbnailUrl: proxiedImageUrl,
        caption: post.caption || '',
        likesCount: (() => {
          const count = post.like_count !== undefined ? post.like_count : (post.likesCount ?? null);
          if (post.like_and_view_counts_disabled || post.likesAndViewCountsDisabled) return null;
          if (count === null || count === 3) return null;
          return count;
        })(),
        commentsCount: post.comment_count || post.commentsCount || 0,
        videoViewCount: post.play_count || post.view_count || post.videoPlayCount || post.videoViewCount || post.videoViews || 0,
        viewCount: post.play_count || post.view_count || post.viewCount || 0,
        timestamp: post.taken_at
          ? (typeof post.taken_at === 'number' ? new Date(post.taken_at * 1000).toISOString() : post.taken_at)
          : (post.timestamp || post.crawled_at),
        type: post.type || post.media_type || (onlyReels ? 'Video' : undefined),
      };
    });

    if (onlyReels) {
      mappedPosts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }

    const result = { success: true, data: mappedPosts };
    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Erro ao buscar posts Instagram:', error.message);
    throw error;
  }
};

export const getTikTokPosts = async (username, limit = 21) => {
  try {
    if (!username) throw new Error('Username é obrigatório');

    const cacheKey = getCacheKey('tiktok-posts', { username, limit });
    const cachedData = getFromCache(cacheKey);
    if (cachedData) return cachedData;

    const tiktokUrl = username.startsWith('http')
      ? username
      : `https://www.tiktok.com/@${username.replace('@', '').toLowerCase()}`;

    const input = {
      profiles: [tiktokUrl],
      resultsPerPage: limit,
      shouldDownloadVideos: false,
      shouldDownloadCovers: false,
      shouldDownloadSubtitles: false,
      shouldDownloadSlideshowImages: false,
      memory: 4096,
    };

    const data = await runApifyActor('clockworks~free-tiktok-scraper', input);

    if (!Array.isArray(data) || data.length === 0) throw new Error('Nenhum post encontrado');

    const sortedPosts = data.sort((a, b) => {
      const timeA = a.createTime ? new Date(a.createTime * 1000).getTime() : 0;
      const timeB = b.createTime ? new Date(b.createTime * 1000).getTime() : 0;
      return timeB - timeA;
    });

    const mappedPosts = sortedPosts.map(post => {
      const imageUrl = post.videoMeta?.coverUrl || post.covers?.default || post.covers?.origin;
      const proxiedImageUrl = imageUrl ? `${API_URL}/proxy-image?url=${encodeURIComponent(imageUrl)}` : null;

      return {
        id: post.id,
        shortCode: post.id,
        url: post.webVideoUrl || `https://www.tiktok.com/@${username}/video/${post.id}`,
        displayUrl: proxiedImageUrl,
        thumbnailUrl: proxiedImageUrl,
        caption: post.text || '',
        likesCount: post.diggCount || 0,
        commentsCount: post.commentCount || 0,
        videoViewCount: post.playCount || 0,
        viewCount: post.playCount || 0,
        timestamp: post.createTime ? new Date(post.createTime * 1000).toISOString() : null,
        type: 'Video',
      };
    });

    const result = { success: true, data: mappedPosts };
    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Erro ao buscar posts TikTok:', error.message);
    throw error;
  }
};

export const getYouTubePosts = async (channelUrl, limit = 21) => {
  try {
    if (!channelUrl) throw new Error('Channel URL é obrigatório');

    const cacheKey = getCacheKey('youtube-posts', { channelUrl, limit });
    const cachedData = getFromCache(cacheKey);
    if (cachedData) return cachedData;

    const input = {
      startUrls: [{ url: channelUrl }],
      maxResults: limit,
      searchMode: 'videos',
      memory: 4096,
    };

    const data = await runApifyActor('streamers~youtube-channel-scraper', input);

    if (!Array.isArray(data) || data.length === 0) throw new Error('Nenhum vídeo encontrado');

    const mappedPosts = data.map(video => {
      const thumbnailUrl = video.thumbnail || video.thumbnailUrl || video.thumbnails?.high?.url || video.thumbnails?.default?.url;
      const proxiedImageUrl = thumbnailUrl ? `${API_URL}/proxy-image?url=${encodeURIComponent(thumbnailUrl)}` : null;

      let viewCount = 0;
      if (video.views) viewCount = parseInt(String(video.views).replace(/[^\d]/g, ''));
      else if (video.viewCount) viewCount = parseInt(String(video.viewCount).replace(/[^\d]/g, ''));

      return {
        id: video.id || video.videoId,
        shortCode: video.id || video.videoId,
        url: video.url || `https://www.youtube.com/watch?v=${video.id || video.videoId}`,
        displayUrl: proxiedImageUrl,
        thumbnailUrl: proxiedImageUrl,
        caption: video.title || video.name || '',
        likesCount: parseInt(video.likes || video.likeCount || 0),
        commentsCount: parseInt(video.numberOfComments || video.commentCount || 0),
        videoViewCount: viewCount,
        viewCount,
        timestamp: video.date || video.uploadDate || video.publishedAt,
        type: 'Video',
      };
    });

    const result = { success: true, data: mappedPosts };
    setCache(cacheKey, result);
    return result;
  } catch (error) {
    console.error('Erro ao buscar vídeos YouTube:', error.message);
    throw error;
  }
};

export const searchInstagram = async (rawUsername, platform = 'instagram') => {
  const username = rawUsername.trim();
  try {
    const cacheKey = getCacheKey('profile', { username, platform });
    const cachedData = getFromCache(cacheKey);
    if (cachedData) return cachedData;

    let actorId, input;

    if (platform === 'youtube') {
      actorId = 'streamers~youtube-channel-scraper';
      // Link completo (suporta /channel/, /c/, /user/, /@handle) é usado tal como está;
      // só reconstruímos a URL quando o utilizador escreveu apenas o nome/@handle.
      const youtubeUrl = username.startsWith('http')
        ? username
        : `https://youtube.com/@${username.replace('@', '').toLowerCase()}`;
      input = { startUrls: [{ url: youtubeUrl }], maxResults: 1, searchMode: 'channel' };
    } else if (platform === 'tiktok') {
      actorId = 'clockworks~tiktok-profile-scraper';
      const tiktokUrl = username.startsWith('http')
        ? username
        : `https://www.tiktok.com/@${username.replace('@', '').toLowerCase()}`;

      input = {
        profiles: [tiktokUrl],
        resultsPerPage: 1,
        memory: 4096,
      };
    } else {
      actorId = 'apify~instagram-profile-scraper';
      input = { usernames: [username], maxRequestRetries: 2, resultsLimit: 1, memory: 4096 };
    }

    const responseData = await runApifyActor(actorId, input);

    if (!Array.isArray(responseData) || responseData.length === 0) {
      throw new Error('Utilizador não encontrado');
    }
    if (responseData[0]?.error) {
      throw new Error('Utilizador não encontrado');
    }

    const data = responseData[0];
    const isPrivate = data.private === true || data.isPrivate === true;

    if (platform === 'youtube') {
      const info = data.aboutChannelInfo || data;
      // Link de canal -> foto do canal; link de vídeo (sem aboutChannelInfo) -> usa a thumbnail do vídeo
      let originalUrl = info.channelAvatarUrl || info.channelPhoto || info.channelThumbnail || info.channelAvatar || info.avatar || info.thumbnail || info.thumbnailUrl;
      const profilePicUrl = originalUrl
        ? `${API_URL}/proxy-image?url=${encodeURIComponent(originalUrl)}`
        : `https://ui-avatars.com/api/?name=${username}&size=200&background=FF0000&color=fff`;

      const cleanHandle = username.startsWith('http')
        ? (username.match(/youtube\.com\/@?([\w.%-]+)/)?.[1] || username)
        : username.replace('@', '');
      const channelName = info.channelName || info.channelUsername || info.channelHandle || info.name || cleanHandle;

      let subscribersCount = 0;
      const subFields = [info.numberOfSubscribers, info.channelSubscribers, info.subscribersCount, info.subscribers, info.subscriberCount, info.followerCount];
      for (const field of subFields) {
        if (field) {
          const n = parseInt(String(field).replace(/[^\d]/g, ''));
          if (n) { subscribersCount = n; break; }
        }
      }

      const videosCount = parseInt(info.channelTotalVideos || info.numberOfVideos || info.videosCount || info.videos || 0);

      const result = {
        success: true,
        data: {
          username: channelName,
          followers: subscribersCount,
          following: 0,
          posts: videosCount,
          profilePic: profilePicUrl,
          verified: info.isChannelVerified || info.verified || false,
          biography: info.channelDescription || info.description || '',
          website: info.channelUrl || data.url || '',
          isPrivate: false,
        },
      };

      setCache(cacheKey, result); // YouTube nunca é privado
      return result;

    } else if (platform === 'tiktok') {
      const authorData = data.authorMeta || data;
      let originalUrl = authorData.avatar || authorData.avatarLarger || authorData.avatarThumb || data.author?.avatar || data.avatarLarger || data.avatarMedium;
      // URLs do TikTok CDN são assinadas e expiram — usar URL direta em vez de proxy
      const profilePicUrl = originalUrl || `https://ui-avatars.com/api/?name=${username}&size=200&background=000000&color=fff`;

      const isTikTokPrivate = authorData.privateAccount === true || authorData.isPrivate === true
        || data.privateAccount === true || data.isPrivate === true || data.private === true;

      const result = {
        success: true,
        data: {
          username: authorData.name || authorData.uniqueId || data.uniqueId || username.replace('@', ''),
          followers: parseInt(authorData.fans || authorData.followerCount || data.followerCount || 0),
          following: parseInt(authorData.following || authorData.followingCount || data.followingCount || 0),
          posts: parseInt(authorData.video || authorData.videoCount || data.videoCount || 0),
          profilePic: profilePicUrl,
          verified: authorData.verified || data.verified || false,
          biography: authorData.signature || data.signature || '',
          website: '',
          isPrivate: isTikTokPrivate,
        },
      };

      if (!isTikTokPrivate) setCache(cacheKey, result);
      return result;

    } else {
      let originalUrl = data.profilePicUrlHD || data.profilePicUrl || data.profile_pic_url_hd || data.profile_pic_url || data.picture || data.avatar;
      let profilePicUrl;
      if (!originalUrl) {
        profilePicUrl = `https://ui-avatars.com/api/?name=${username}&size=200&background=E1306C&color=fff`;
      } else {
        const decodedUrl = decodeApifyUrl(originalUrl);
        profilePicUrl = `${API_URL}/proxy-image?url=${encodeURIComponent(decodedUrl)}`;
      }

      const result = {
        success: true,
        data: {
          username: data.username || username,
          followers: parseInt(data.followersCount) || 0,
          following: parseInt(data.followsCount) || 0,
          posts: parseInt(data.postsCount) || 0,
          profilePic: profilePicUrl,
          verified: data.verified || false,
          biography: data.biography || '',
          website: data.website || '',
          isPrivate,
        },
      };

      if (!isPrivate) setCache(cacheKey, result);
      return result;
    }
  } catch (error) {
    console.error(`Erro ao buscar ${platform}:`, error.message);
    throw error;
  }
};
